import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Trash2, CheckCircle2, XCircle, Loader2, Copy, AlertTriangle, Save, Paperclip, CalendarDays,
} from 'lucide-react';
import { format, eachDayOfInterval, startOfDay } from 'date-fns';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { useToast } from '@/hooks/use-toast';
import { taskService, type Task } from '@/services/task.service';
import { sprintService, type SprintDetail, type SprintGoal, type SprintGoalWithTasks } from '@/services/sprint.service';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface TempTask {
  id: string;
  title: string;
  description: string;
  assignee_id: string;
  start_date: Date | null;
  end_date: Date | null;
  priority: string;
  estimated_hours: number | null;
  sprint_goal_ids: string[];
  isValid: boolean;
}

function createEmptyTask(): TempTask {
  return {
    id: crypto.randomUUID(),
    title: '',
    description: '',
    assignee_id: '',
    start_date: null,
    end_date: null,
    priority: 'medium',
    estimated_hours: null,
    sprint_goal_ids: [],
    isValid: false,
  };
}

function validateTask(t: TempTask): boolean {
  return t.title.trim() !== '' && t.assignee_id !== '' && t.start_date !== null && t.end_date !== null;
}

// ---------------------------------------------------------------------------
// Utilization helpers
// ---------------------------------------------------------------------------
const DEFAULT_WORKING_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

function isWorkingDay(date: Date, workingDays: string[]): boolean {
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return workingDays.includes(dayNames[date.getDay()]);
}

function countWorkingDaysInRange(start: Date, end: Date, workingDays: string[]): number {
  if (start > end) return 0;
  return eachDayOfInterval({ start: startOfDay(start), end: startOfDay(end) })
    .filter((d) => isWorkingDay(d, workingDays)).length;
}

function computeSprintCapacity(
  sprintStart: string,
  sprintEnd: string,
  workingDays: string[],
  dailyHours: number,
): number {
  return countWorkingDaysInRange(new Date(sprintStart), new Date(sprintEnd), workingDays) * dailyHours;
}

function computeAssignedHoursInSprint(
  tasks: { start_date: string | null; end_date: string | null; estimated_hours: number | null; assignee_id: string | null }[],
  userId: string,
  sprintStart: string,
  sprintEnd: string,
  workingDays: string[],
): number {
  const ss = startOfDay(new Date(sprintStart));
  const se = startOfDay(new Date(sprintEnd));
  let total = 0;
  for (const t of tasks) {
    if (t.assignee_id !== userId || !t.start_date || !t.end_date || !t.estimated_hours) continue;
    const ts = startOfDay(new Date(t.start_date));
    const te = startOfDay(new Date(t.end_date));
    // overlap
    const overlapStart = ts > ss ? ts : ss;
    const overlapEnd = te < se ? te : se;
    if (overlapStart > overlapEnd) continue;
    const taskWorkingDays = countWorkingDaysInRange(ts, te, workingDays);
    if (taskWorkingDays <= 0) { total += t.estimated_hours; continue; }
    const overlapWorkingDays = countWorkingDaysInRange(overlapStart, overlapEnd, workingDays);
    total += t.estimated_hours * (overlapWorkingDays / taskWorkingDays);
  }
  return total;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface TasksPlannerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sprintId: string;
  columnId: string;
  columnName: string;
  sprint: SprintDetail;
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export function TasksPlanner({ open, onOpenChange, sprintId, columnId, columnName, sprint }: TasksPlannerProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [tasks, setTasks] = useState<TempTask[]>([createEmptyTask()]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progressState, setProgressState] = useState({ current: 0, total: 0 });

  const members = sprint.members || [];
  const goals = sprint.sprint_goals || [];
  const validCount = tasks.filter((t) => t.isValid).length;

  // Fetch existing sprint tasks for utilization calculations
  const { data: existingTasksData } = useQuery({
    queryKey: ['sprint-tasks', sprintId],
    queryFn: () => taskService.list({ sprint_id: sprintId, limit: 500 }).then((r) => r.data),
    enabled: open,
  });
  const existingTasks: Task[] = existingTasksData?.data ?? [];

  // Fetch user's tasks from OTHER sources that overlap with sprint dates
  const { data: allUserTasksData } = useQuery({
    queryKey: ['all-tasks-for-planner', sprintId],
    queryFn: () => taskService.list({ limit: 500 }).then((r) => r.data),
    enabled: open,
  });
  const allTasks: Task[] = allUserTasksData?.data ?? [];

  // Fetch goals with their linked tasks (for coverage counting)
  const { data: goalsWithTasksData } = useQuery({
    queryKey: ['sprint-goals-tasks', sprintId],
    queryFn: () => sprintService.getGoalsWithTasks(sprintId).then((r) => r.data.data),
    enabled: open,
  });
  const goalsWithTasks: SprintGoalWithTasks[] = goalsWithTasksData ?? [];

  // ---------------------------------------------------------------------------
  // Row operations
  // ---------------------------------------------------------------------------
  function updateTask(id: string, updates: Partial<TempTask>) {
    setTasks((prev) =>
      prev.map((t) => {
        if (t.id !== id) return t;
        const updated = { ...t, ...updates };
        updated.isValid = validateTask(updated);
        return updated;
      }),
    );
  }

  function addRow() {
    setTasks((prev) => [...prev, createEmptyTask()]);
  }

  function duplicateRow(sourceId: string) {
    setTasks((prev) => {
      const source = prev.find((t) => t.id === sourceId);
      if (!source) return prev;
      const dup: TempTask = {
        ...source,
        id: crypto.randomUUID(),
        title: '', // empty title so user fills it
        isValid: false,
      };
      const idx = prev.findIndex((t) => t.id === sourceId);
      const next = [...prev];
      next.splice(idx + 1, 0, dup);
      return next;
    });
  }

  function removeRow(id: string) {
    setTasks((prev) => prev.filter((t) => t.id !== id));
  }

  function clearAll() {
    setTasks([createEmptyTask()]);
  }

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------
  async function submitTasks() {
    const valid = tasks.filter((t) => t.isValid);
    if (valid.length === 0) return;

    setIsSubmitting(true);
    setProgressState({ current: 0, total: valid.length });

    let success = 0;
    let failed = 0;

    for (let i = 0; i < valid.length; i++) {
      const t = valid[i];
      try {
        await taskService.create({
          title: t.title,
          description: t.description || undefined,
          task_type: 'sprint',
          sprint_id: sprintId,
          column_id: columnId,
          assignee_id: t.assignee_id,
          priority: t.priority,
          start_date: format(t.start_date!, 'yyyy-MM-dd'),
          end_date: format(t.end_date!, 'yyyy-MM-dd'),
          estimated_hours: t.estimated_hours || undefined,
          sprint_goal_ids: t.sprint_goal_ids.length > 0 ? t.sprint_goal_ids : undefined,
        });
        success++;
      } catch {
        failed++;
      }
      setProgressState({ current: i + 1, total: valid.length });
    }

    setIsSubmitting(false);
    queryClient.invalidateQueries({ queryKey: ['sprint-tasks', sprintId] });
    queryClient.invalidateQueries({ queryKey: ['sprint', sprintId] });
    queryClient.invalidateQueries({ queryKey: ['sprint-goals-tasks', sprintId] });

    if (failed === 0) {
      toast({ title: `Created ${success} task(s)` });
      onOpenChange(false);
      setTasks([createEmptyTask()]);
    } else {
      toast({ title: `Created ${success}, failed ${failed}`, variant: 'destructive' });
    }
  }

  // ---------------------------------------------------------------------------
  // Save as Draft (persist to localStorage)
  // ---------------------------------------------------------------------------
  function saveDraft() {
    const key = `planner-draft-${sprintId}-${columnId}`;
    localStorage.setItem(key, JSON.stringify(tasks));
    toast({ title: 'Draft saved', description: 'Your task rows have been saved locally.' });
  }

  // Restore draft on mount
  useState(() => {
    const key = `planner-draft-${sprintId}-${columnId}`;
    const stored = localStorage.getItem(key);
    if (stored) {
      try {
        const parsed: TempTask[] = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const restored = parsed.map((t) => ({
            ...t,
            start_date: t.start_date ? new Date(t.start_date) : null,
            end_date: t.end_date ? new Date(t.end_date) : null,
            isValid: validateTask({
              ...t,
              start_date: t.start_date ? new Date(t.start_date) : null,
              end_date: t.end_date ? new Date(t.end_date) : null,
            }),
          }));
          setTasks(restored);
          localStorage.removeItem(key);
        }
      } catch { /* ignore */ }
    }
  });

  // ---------------------------------------------------------------------------
  // Team Utilization data
  // ---------------------------------------------------------------------------
  interface MemberUtil {
    name: string;
    avatar?: string;
    workingDays: string[];
    dailyHours: number;
    totalCapacity: number;
    sprintHours: number;
    otherHours: number;
    draftHours: number;
  }

  const memberUtils: Record<string, MemberUtil> = {};
  for (const m of members) {
    const uid = m.user?.id || '';
    if (!uid) continue;
    const wd = m.user?.working_days ?? DEFAULT_WORKING_DAYS;
    const dh = m.user?.daily_hours ?? 8;
    const capacity = computeSprintCapacity(sprint.start_date, sprint.end_date, wd, dh);

    // Sprint hours from existing tasks
    const sprintHrs = computeAssignedHoursInSprint(
      existingTasks, uid, sprint.start_date, sprint.end_date, wd,
    );

    // Other hours from tasks NOT in this sprint
    const otherTasks = allTasks.filter((t) => t.sprint_id !== sprintId && t.assignee_id === uid);
    const otherHrs = computeAssignedHoursInSprint(
      otherTasks, uid, sprint.start_date, sprint.end_date, wd,
    );

    memberUtils[uid] = {
      name: m.user?.full_name || 'Unknown',
      avatar: m.user?.avatar_url || undefined,
      workingDays: wd,
      dailyHours: dh,
      totalCapacity: capacity,
      sprintHours: sprintHrs,
      otherHours: otherHrs,
      draftHours: 0,
    };
  }

  // Add draft hours from planner rows
  for (const t of tasks) {
    if (t.assignee_id && t.estimated_hours && memberUtils[t.assignee_id]) {
      memberUtils[t.assignee_id].draftHours += t.estimated_hours;
    }
  }

  // ---------------------------------------------------------------------------
  // Goals coverage (existing DB tasks + draft planner rows)
  // ---------------------------------------------------------------------------
  interface GoalCoverage {
    existingCount: number;
    draftCount: number;
    total: number;
  }
  const goalCoverage: Record<string, GoalCoverage> = {};
  for (const g of goals) {
    goalCoverage[g.id] = { existingCount: 0, draftCount: 0, total: 0 };
  }

  // Count existing tasks per goal from goalsWithTasks (filtered by current column)
  for (const gwt of goalsWithTasks) {
    if (!goalCoverage[gwt.id]) continue;
    const tasksInColumn = (gwt.tasks ?? []).filter((t) => t.column_id === columnId);
    goalCoverage[gwt.id].existingCount = tasksInColumn.length;
  }

  // Count draft planner rows per goal
  for (const t of tasks) {
    for (const gid of t.sprint_goal_ids) {
      if (goalCoverage[gid]) {
        goalCoverage[gid].draftCount += 1;
      }
    }
  }

  // Compute totals
  for (const gid of Object.keys(goalCoverage)) {
    goalCoverage[gid].total = goalCoverage[gid].existingCount + goalCoverage[gid].draftCount;
  }

  const uncoveredGoals = goals.filter((g) => (goalCoverage[g.id]?.total ?? 0) === 0);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] w-full h-full flex flex-col">
        <DialogHeader>
          <DialogTitle>
            Tasks Planner — {columnName}
            <span className="text-sm font-normal text-muted-foreground ml-2">
              ({validCount} of {tasks.length} rows valid)
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 gap-4 overflow-hidden">
          {/* ── Left sidebar: Team Utilization ── */}
          <div className="w-56 shrink-0 border rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-muted/30 border-b">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Team Utilization</h4>
              <p className="text-[10px] text-muted-foreground">Includes all tasks during sprint</p>
            </div>
            <ScrollArea className="h-full max-h-[60vh]">
              <div className="p-2 space-y-2">
                {Object.entries(memberUtils).map(([uid, info]) => {
                  const assigned = info.sprintHours + info.draftHours + info.otherHours;
                  const pct = info.totalCapacity > 0 ? Math.round((assigned / info.totalCapacity) * 100) : 0;
                  const barPct = Math.min(100, pct);

                  return (
                    <div key={uid} className="p-2 rounded-lg bg-muted/20 text-xs space-y-1.5">
                      <div className="flex items-center gap-2">
                        <UserAvatar name={info.name} avatarUrl={info.avatar} size="xs" />
                        <span className="font-medium truncate">{info.name}</span>
                      </div>

                      {/* Hours summary line */}
                      <div className="flex items-center justify-between text-muted-foreground">
                        <span>{assigned.toFixed(1)}h / {info.totalCapacity.toFixed(0)}h</span>
                        <span className={cn(
                          'font-semibold',
                          pct <= 80 ? 'text-emerald-600' : pct <= 100 ? 'text-amber-600' : 'text-red-600',
                        )}>
                          {pct}%
                        </span>
                      </div>

                      {/* Progress bar */}
                      <Progress
                        value={barPct}
                        className={cn('h-1.5', pct > 100 && '[&>div]:bg-red-500', pct > 80 && pct <= 100 && '[&>div]:bg-amber-500')}
                      />

                      {/* Breakdown */}
                      <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-[10px]">
                        <span className="text-muted-foreground">{info.sprintHours.toFixed(1)}h sprint</span>
                        {info.draftHours > 0 && (
                          <span className="text-brand-primary">+{info.draftHours.toFixed(1)}h draft</span>
                        )}
                        {info.otherHours > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-red-600">
                            <CalendarDays className="h-3 w-3" />
                            {info.otherHours.toFixed(1)}h other
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>

          {/* ── Main table area ── */}
          <div className="flex-1 overflow-auto border rounded-lg">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-background z-10">
                <tr className="border-b bg-muted/30">
                  <th className="px-2 py-2 text-left text-xs font-medium w-8"></th>
                  <th className="px-2 py-2 text-left text-xs font-medium">Title *</th>
                  <th className="px-2 py-2 text-left text-xs font-medium w-32">Description</th>
                  <th className="px-2 py-2 text-left text-xs font-medium w-36">Assignee *</th>
                  <th className="px-2 py-2 text-left text-xs font-medium w-28">Start *</th>
                  <th className="px-2 py-2 text-left text-xs font-medium w-28">End *</th>
                  <th className="px-2 py-2 text-left text-xs font-medium w-24">Priority</th>
                  <th className="px-2 py-2 text-left text-xs font-medium w-20">Hours</th>
                  <th className="px-2 py-2 text-left text-xs font-medium w-24">Goals</th>
                  <th className="px-2 py-2 text-left text-xs font-medium w-20">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => (
                  <PlannerRow
                    key={task.id}
                    task={task}
                    members={members}
                    goals={goals}
                    onUpdate={(updates) => updateTask(task.id, updates)}
                    onDuplicate={() => duplicateRow(task.id)}
                    onRemove={() => removeRow(task.id)}
                  />
                ))}
              </tbody>
            </table>

            <div className="p-2">
              <Button variant="ghost" size="sm" onClick={addRow} className="text-xs">
                <Plus className="mr-1 h-3 w-3" /> Add Row
              </Button>
            </div>
          </div>
        </div>

        {/* ── Bottom: Sprint Goals Coverage ── */}
        {goals.length > 0 && (
          <div className="border rounded-lg p-3 mt-2">
            <div className="flex items-center gap-2 mb-2">
              <h4 className="text-xs font-semibold">Sprint Goals Coverage</h4>
              {uncoveredGoals.length > 0 && (
                <Badge variant="destructive" className="text-[10px]">{uncoveredGoals.length} uncovered</Badge>
              )}
            </div>
            <div className="space-y-1.5">
              {goals.map((g) => {
                const cov = goalCoverage[g.id] ?? { existingCount: 0, draftCount: 0, total: 0 };
                const isCovered = cov.total > 0;
                return (
                  <div
                    key={g.id}
                    className={cn(
                      'flex items-center gap-2 text-xs px-3 py-2 rounded border',
                      isCovered ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50',
                    )}
                  >
                    {/* Warning / check icon */}
                    {isCovered ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                    ) : (
                      <AlertTriangle className="h-3.5 w-3.5 text-red-500 shrink-0" />
                    )}

                    {/* Goal title */}
                    <span className={cn('flex-1 truncate', !isCovered && 'text-red-700')}>
                      {g.title}
                    </span>

                    {/* Phase checkboxes B D T U */}
                    <div className="flex items-center gap-1 shrink-0">
                      <PhaseCheck label="B" checked={g.business_ready} />
                      <PhaseCheck label="D" checked={g.development_done} />
                      <PhaseCheck label="T" checked={g.testing_completed} />
                      <PhaseCheck label="U" checked={g.uat_approved} />
                    </div>

                    {/* Attachment icon */}
                    <Paperclip className="h-3 w-3 text-muted-foreground shrink-0" />

                    {/* Task count badge: "X tasks (+Y)" where Y is draft additions */}
                    <Badge
                      variant={isCovered ? 'secondary' : 'destructive'}
                      className={cn('text-[10px] shrink-0', isCovered && 'bg-emerald-100 text-emerald-700')}
                    >
                      {cov.total} task{cov.total !== 1 ? 's' : ''}
                      {cov.draftCount > 0 && (
                        <span className="ml-0.5 text-muted-foreground">(+{cov.draftCount})</span>
                      )}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Footer ── */}
        <DialogFooter className="flex items-center justify-between sm:justify-between gap-2">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={clearAll}>Clear All</Button>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="gap-1" onClick={saveDraft}>
              <Save className="h-3.5 w-3.5" />
              Save as Draft
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              className="bg-brand-primary hover:bg-brand-primary-dark"
              onClick={submitTasks}
              disabled={validCount === 0 || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {progressState.current}/{progressState.total}
                </>
              ) : (
                `Submit ${validCount} Task${validCount !== 1 ? 's' : ''}`
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Phase check mini-component for goal coverage
// ---------------------------------------------------------------------------
function PhaseCheck({ label, checked }: { label: string; checked: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center h-5 w-5 rounded text-[10px] font-semibold border',
        checked
          ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
          : 'bg-muted/50 text-muted-foreground border-border',
      )}
    >
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Planner Row
// ---------------------------------------------------------------------------
function PlannerRow({
  task, members, goals, onUpdate, onDuplicate, onRemove,
}: {
  task: TempTask;
  members: SprintDetail['members'];
  goals: SprintGoal[];
  onUpdate: (updates: Partial<TempTask>) => void;
  onDuplicate: () => void;
  onRemove: () => void;
}) {
  const [goalsOpen, setGoalsOpen] = useState(false);
  const hasContent = task.title || task.assignee_id || task.start_date || task.end_date;

  return (
    <tr className={cn('border-b', task.isValid ? 'bg-emerald-50/30' : '')}>
      {/* Validation indicator */}
      <td className="px-2 py-1.5 text-center">
        {task.isValid ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-500 mx-auto" />
        ) : hasContent ? (
          <XCircle className="h-4 w-4 text-red-400 mx-auto" />
        ) : null}
      </td>

      {/* Title */}
      <td className="px-2 py-1.5">
        <Input
          className="h-8 text-xs"
          placeholder="Task title"
          value={task.title}
          onChange={(e) => onUpdate({ title: e.target.value })}
        />
      </td>

      {/* Description */}
      <td className="px-2 py-1.5">
        <Input
          className="h-8 text-xs"
          placeholder="Description"
          value={task.description}
          onChange={(e) => onUpdate({ description: e.target.value })}
        />
      </td>

      {/* Assignee */}
      <td className="px-2 py-1.5">
        <Select value={task.assignee_id || undefined} onValueChange={(v) => onUpdate({ assignee_id: v })}>
          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>
            {members.map((m) => (
              <SelectItem key={m.user?.id || m.id} value={m.user?.id || m.id}>
                {m.user?.full_name || 'Unknown'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>

      {/* Start Date */}
      <td className="px-2 py-1.5">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="h-8 text-xs w-full justify-start">
              {task.start_date ? format(task.start_date, 'MMM dd') : 'Start'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" selected={task.start_date || undefined} onSelect={(d) => onUpdate({ start_date: d || null })} initialFocus />
          </PopoverContent>
        </Popover>
      </td>

      {/* End Date */}
      <td className="px-2 py-1.5">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="h-8 text-xs w-full justify-start">
              {task.end_date ? format(task.end_date, 'MMM dd') : 'End'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" selected={task.end_date || undefined} onSelect={(d) => onUpdate({ end_date: d || null })} initialFocus />
          </PopoverContent>
        </Popover>
      </td>

      {/* Priority */}
      <td className="px-2 py-1.5">
        <Select value={task.priority} onValueChange={(v) => onUpdate({ priority: v })}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
          </SelectContent>
        </Select>
      </td>

      {/* Hours (number spinner) */}
      <td className="px-2 py-1.5">
        <Input
          className="h-8 text-xs"
          type="number"
          step="0.5"
          min="0"
          placeholder="0"
          value={task.estimated_hours ?? ''}
          onChange={(e) => onUpdate({ estimated_hours: e.target.value ? parseFloat(e.target.value) : null })}
        />
      </td>

      {/* Goals */}
      <td className="px-2 py-1.5">
        <Popover open={goalsOpen} onOpenChange={setGoalsOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="h-8 text-xs w-full justify-start">
              {task.sprint_goal_ids.length > 0 ? `${task.sprint_goal_ids.length} goals` : 'None'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-2" align="start">
            <div className="space-y-1">
              {goals.map((g) => (
                <label key={g.id} className="flex items-center gap-2 text-xs p-1 rounded hover:bg-muted cursor-pointer">
                  <Checkbox
                    checked={task.sprint_goal_ids.includes(g.id)}
                    onCheckedChange={() => {
                      const ids = task.sprint_goal_ids.includes(g.id)
                        ? task.sprint_goal_ids.filter((id) => id !== g.id)
                        : [...task.sprint_goal_ids, g.id];
                      onUpdate({ sprint_goal_ids: ids });
                    }}
                  />
                  <span className="truncate">{g.title}</span>
                </label>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </td>

      {/* Actions: Duplicate + Delete */}
      <td className="px-2 py-1.5">
        <div className="flex items-center gap-0.5">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
            onClick={onDuplicate}
            title="Duplicate row"
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
            onClick={onRemove}
            title="Delete row"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </td>
    </tr>
  );
}
